/**
 * Displays discount details based on the Splend Initiative type
 *
 * Props: pricingRecord, splendInitiative, pricingRecordLineItems
 * API Call: based on the included discount coupon stored in pricing record,
 *      get Chargebee information via API call
 * */

import React, {useEffect} from "react";

import {UserFormStates, useUserJourneyContext} from '@context/UserJourneyContext';
import { InfoBox } from "@components/cards/messageBox";
import Label from "@components/inputs/text/Label";
import { PanelItem, IPanelItem } from "@components/panels/PanelItems";
import { SplendInitiative } from "@services/customer/CustomerPotentials";
import { CBPOnboard } from "@context/UserJourneyContext";
import { PricingRecordLineItem } from "@services/quotes/PricingRecords";

// Pass the pricingRecord as a prop instead of accessing through context
// to make the component more reusable and testable.
interface DiscountViewProps {
  splendInitiative?: SplendInitiative;
  pricingRecord?: CBPOnboard;
}

interface DiscountConfig {
  title: string | null;
  testId: string;
  items: IPanelItem[];
  showNoDiscount?: boolean;
}

// Function for extracting coupon details from pricingRecord.
// Separated out this logic so it can be testable
const getIncludedCouponFromPricingRecord = (
  pricingRecordLineItems: PricingRecordLineItem[] | any
): Array<any> => {
  if (
    !pricingRecordLineItems ||
    !Array.isArray(pricingRecordLineItems) ||
    pricingRecordLineItems.length === 0
  ) {
    return [];
  }
  const includedCoupons = pricingRecordLineItems.filter(
    (item: PricingRecordLineItem) =>
      item.chargebee_item_type === "Coupon" && item.included === true
  );
  return includedCoupons;
};

export default function DiscountView({
  splendInitiative,
  pricingRecord,
}: DiscountViewProps) {
  // Need to store some information in the context to be used in contract generation
  const {formState, updateFormState} = useUserJourneyContext();

  // Extracting included coupons only from pricing record line items
  const couponsFromPricingRecord = getIncludedCouponFromPricingRecord(
    pricingRecord?.quoteAddons
  );

  useEffect(() => {
    // The computation of the discounted weekly price is calculated here because 
    // the generation of the invoice preview and the contract uses the
    // splendInitiatives in the context.
    if (couponsFromPricingRecord && couponsFromPricingRecord.length > 0) {
      if(splendInitiative === SplendInitiative.Ignition) {
        updateFormState(UserFormStates.splendInitiatives, {
          additionalDiscount: Math.abs(couponsFromPricingRecord[0].price_including_gst),  // This is used in the invoice preview that's why we have to update it here
        });
      }
      else if (splendInitiative === SplendInitiative.DynamicPricing) {
        // Price of the coupon in the pricing record is negative that's why
        // we are using Math.abs to get the absolute value.
        // NOTE: This logic assumes only one coupon for Dynamic Pricing
        const totalDiscount = Math.abs(couponsFromPricingRecord[0].price_including_gst);
        updateFormState(UserFormStates.splendInitiatives, {
          totalDynamicPricing: totalDiscount,  // This is used in the invoice preview that's why we have to update it here
        });
      }
    }

  }, []); // Empty dependencies so that this runs only once on mount
  
  const getDiscountConfig = (): DiscountConfig => {
    // This function is to determine which discount details to show based on the
    // Splend Initiative. If there will be a new Splend initiative that requires
    // different details to show, just add a new case in the switch statement
    // below. To add more fields to the response, make sure to update the
    // DiscountConfig interface above.
    switch (splendInitiative) {
      case SplendInitiative.DynamicPricing:
        return {
          title: "Dynamic Pricing - details",
          testId: "dynamicPricingDetailsPanel",
          items: [
            {
              header: "Minimum Term (weeks)",
              headerTextStyleCfg: { bold: true },
              value: pricingRecord?.MinimumTerm || "-",
            },
            {
              header: "Trip Count",
              headerTextStyleCfg: { bold: true },
              value: pricingRecord?.TripCount || "-",
            },
          ],
        };

      case SplendInitiative.Ignition:
        return {
          title: "Ignition - details",
          testId: "ignitionDetailsPanel",
          items: [
            {
              header: "Minimum Term (weeks)",
              headerTextStyleCfg: { bold: true },
              value: pricingRecord?.MinimumTerm || "",
            },
          ],
        };

      default:
        return {
          title: null,
          testId: "defaultDetailsPanel",
          items: [
            {
              header: "Minimum Term (weeks)",
              headerTextStyleCfg: { bold: true },
              value: pricingRecord?.MinimumTerm || "",
            },
          ],
          showNoDiscount: true,
        };
    }
  };
  const config = getDiscountConfig();

  return (
    <>
      <InfoBox message="Data based on sales quote" />
      <Section>
        {config.showNoDiscount && (
          <div data-testid="noDiscountMessage">
            <Label text="No discount applied." />
          </div>
        )}
        {config.title && (
          <Label
            text={config.title}
            styleCfg={{ bold: true, largeFont: true }}
          />
        )}
        <div
          style={{ display: "flex", flexDirection: "column" }}
          data-testid={config.testId}
        >
          {config.items.map((itemProps, index) => (
            <PanelItem key={`${config.testId}-${index}`} {...itemProps} />
          ))}
        </div>
      </Section>
      {/* Coupon details only for ignition and dynamic pricing */}
      <Section>
        {(splendInitiative === SplendInitiative.DynamicPricing ||
          splendInitiative === SplendInitiative.Ignition) && (
          <>
            {/* For each coupon in couponDetails, render the panel items below */}
            {couponsFromPricingRecord?.map((coupon: any, index: number) => (
              <div key={index} style={{ marginBottom: "2vh" }}>
                <PanelItem
                  header="Coupon name"
                  headerTextStyleCfg={{ bold: true }}
                  value={coupon.chargebee_item_name || "N/A"}
                />
                <PanelItem
                  header="Coupon duration"
                  headerTextStyleCfg={{ bold: true }}
                  value={coupon.coupon_duration_type || "N/A"}
                />
              </div>
            ))}
          </>
        )}
      </Section>
    </>
  );
}

/**
 * Helper components
 */
const Section = ({ children }: { children: React.ReactNode }) => (
  <div style={{ margin: "2.5vh 0vw" }}>{children}</div>
);
